/**
 * EcosystemsPage: the platform apex's single browsing surface for hosted
 * ecosystems, presenting a card view and an "ecosystem of ecosystems" graph
 * view behind one route (`/ecosystems?view=cards|graph`).
 *
 * An APEX (platform-mode) surface: it lives on `rileconnect.org` and is NOT
 * tenant-scoped: it consumes the platform-level `GET /api/ecosystems`
 * endpoint, which lists across all ecosystems by design.
 *
 * Merges the former EcosystemDirectory (`/ecosystems`) and EcosystemGraphPage
 * (`/ecosystems/graph`) pages (#241): both fetched the same ecosystem list
 * independently and kept their own page shells. This container fetches once
 * per page load and swaps the view locally, following the tab pattern the
 * tenant Directory page established (local viewMode state + a Bootstrap
 * `Nav variant="tabs"` control + useSearchParams syncing `?view=` so the
 * active view is bookmarkable and restorable).
 *
 * COSTEM is the only hosted ecosystem today, so the card view shows a single
 * card and the graph a single node: that is the expected v1 state. The
 * grid, graph, and loading / empty / error handling are all built for the
 * goal state of many ecosystems.
 */
import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Container, Nav, Spinner, Alert } from 'react-bootstrap';

import SEO from '@/components/common/SEO';
import { useEcosystemApi } from '@/shared/services/ecosystemApi';

import EcosystemCardsView from './EcosystemCardsView';
import EcosystemGraphView from './EcosystemGraphView';

// The valid `?view=` values. Anything else falls back to the default.
const VALID_VIEWS = ['cards', 'graph'];

export default function EcosystemsPage() {
    const ecosystemApi = useEcosystemApi();
    const [searchParams, setSearchParams] = useSearchParams();

    // ---- View Mode ----
    // Determines which content panel is shown: 'cards' or 'graph'.
    // URL ?view= param takes precedence if valid; cards is the default (the
    // more informative view while few ecosystems are hosted). Unlike the
    // tenant Directory page there is no capability gate here: this surface
    // is platform-level, so the graph tab is always available.
    const getDefaultView = () => {
        const urlView = searchParams.get('view');
        return VALID_VIEWS.includes(urlView) ? urlView : 'cards';
    };

    const [viewMode, setViewMode] = useState(getDefaultView);

    // Sync view mode from URL so back/forward and in-app links to
    // `/ecosystems?view=graph` switch the tab without a remount.
    const urlView = searchParams.get('view');
    useEffect(() => {
        if (VALID_VIEWS.includes(urlView) && urlView !== viewMode) {
            setViewMode(urlView);
        }
    }, [urlView]); // eslint-disable-line

    const handleViewChange = (mode) => {
        setViewMode(mode);
        const newParams = new URLSearchParams(searchParams);
        newParams.set('view', mode);
        setSearchParams(newParams);
    };

    // ---- Data ----
    // Fetched once per page load; both views render from the same list, so
    // switching tabs never refetches.
    const [ecosystems, setEcosystems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                const data = await ecosystemApi.fetchEcosystems();
                if (!cancelled) {
                    setEcosystems(Array.isArray(data) ? data : []);
                }
            } catch (err) {
                if (!cancelled) {
                    setError('Failed to load hosted ecosystems.');
                    console.error(err);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        load();

        return () => {
            cancelled = true;
        };
    }, [ecosystemApi]);

    return (
        <>
            <SEO
                title="Ecosystems"
                description="Browse the ecosystems hosted on the RILE Connect platform, as cards or as an interactive graph."
                breadcrumbs={[
                    { name: 'Home', path: '/' },
                    { name: 'Ecosystems', path: '/ecosystems' },
                ]}
            />
            <Container className="py-5">
                <h1 className="text-primary mb-2">Ecosystems</h1>
                <p className="lead mb-4">
                    Each ecosystem on RILE Connect runs its own members,
                    content, and network visualization. Browse the hosted
                    ecosystems as cards, or explore them as an interactive
                    graph.
                </p>

                {/* View Toggle Tabs (same pattern as the tenant Directory) */}
                <Nav
                    variant="tabs"
                    activeKey={viewMode}
                    onSelect={(k) => handleViewChange(k)}
                    className="mb-4"
                >
                    <Nav.Item>
                        <Nav.Link eventKey="cards">
                            <span className="me-2">📋</span>
                            Card View
                        </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link eventKey="graph">
                            <span className="me-2">🕸️</span>
                            Graph View
                        </Nav.Link>
                    </Nav.Item>
                </Nav>

                {loading && (
                    <div className="text-center py-5">
                        <Spinner animation="border" variant="primary" />
                        <p className="mt-3 text-muted">
                            Loading hosted ecosystems…
                        </p>
                    </div>
                )}

                {!loading && error && (
                    <Alert variant="danger">
                        <Alert.Heading>Unable to load ecosystems</Alert.Heading>
                        <p className="mb-0">{error}</p>
                    </Alert>
                )}

                {!loading && !error && ecosystems.length === 0 && (
                    <div className="text-center py-5">
                        <p className="text-muted mb-0">
                            No ecosystems are hosted on the platform yet.
                        </p>
                    </div>
                )}

                {!loading && !error && ecosystems.length > 0 && (
                    <>
                        {viewMode === 'cards' && (
                            <EcosystemCardsView ecosystems={ecosystems} />
                        )}
                        {viewMode === 'graph' && (
                            <EcosystemGraphView ecosystems={ecosystems} />
                        )}
                    </>
                )}
            </Container>
        </>
    );
}
